// ============================================================================
// image-text-documents.ts: the document rows that hold text read from images
// ----------------------------------------------------------------------------
// Pure helpers, no AI and no storage. Two shapes:
//
//   1. An image that IS a document (an RFQ photo, a scan, a screenshot) is an
//      `isImageDoc` row in documents[]: its bytes in `data`, its text in
//      `content`. Stage 1's handleDocsFiles builds it.
//   2. A site photo that ALSO shows writing ("both") stays in photos[] and
//      gets a derived text row here, linked by `sourcePhotoId`. The photo is
//      never moved or removed.
//
// Editing the text in the Reference Files viewer writes `content` (and
// `textContent`, and `text` when the row already uses it) on that row, so the
// Summary and the quote read the corrected words.
// ============================================================================

export const IMAGE_TEXT_DOCUMENT_ID_PREFIX = 'image-text:';

export interface ImageTextMeta {
    schemaVersion: 1;
    source: 'photo' | 'document';
    photoId?: string;
    /** 'both' = found at import; 'read' = the viewer's Read text action. */
    kind?: 'both' | 'read' | 'document';
    readAt?: string;
    edited?: boolean;
    editedAt?: string;
}

export function photoDisplayName(photo: any): string {
    return String(photo?.name || photo?.originalName || photo?.fileName || 'Photo');
}

export function photoTextDocumentId(photoId: unknown): string {
    return `${IMAGE_TEXT_DOCUMENT_ID_PREFIX}${String(photoId || '')}`;
}

/** The derived text row for a photo whose image shows writing. */
export function buildPhotoTextDocument({
    photo,
    text,
    kind,
    now = new Date(),
}: {
    photo: any;
    text: string;
    kind: 'both' | 'read';
    now?: Date;
}): Record<string, any> {
    const photoId = String(photo?.id || '');
    const name = `${photoDisplayName(photo)} (text)`;
    const imageText: ImageTextMeta = {
        schemaVersion: 1,
        source: 'photo',
        photoId,
        kind,
        readAt: now.toISOString(),
    };
    return {
        id: photoTextDocumentId(photoId),
        name,
        originalName: name,
        ext: 'txt',
        type: 'reference',
        fileKind: 'text',
        contentType: 'text/plain',
        mimeType: 'text/plain',
        content: text,
        textContent: text,
        size: text.length,
        sourcePhotoId: photoId,
        imageText,
        addedAt: now.getTime(),
    };
}

/** The text row read from this photo, or null. */
export function findPhotoTextDocument(documents: unknown, photo: any): any | null {
    const photoId = photo?.id == null ? '' : String(photo.id);
    if (!photoId || !Array.isArray(documents)) return null;
    return documents.find((doc: any) => doc && (
        String(doc.sourcePhotoId || '') === photoId
        || String(doc.id || '') === photoTextDocumentId(photoId)
    )) || null;
}

/**
 * An image document: an imported image kept as a document, or a row whose
 * stored content type is an image. The same test as the Reference Files
 * viewer's getDocMeta; the file name plays no part.
 */
export function isImageReferenceDocument(doc: any): boolean {
    if (!doc || typeof doc !== 'object') return false;
    if (doc.isImageDoc) return true;
    const mime = String(doc.contentType || doc.mimeType || '').toLowerCase();
    return mime.startsWith('image/');
}

/** The text stored on a row, in the order the viewers read it. */
export function readImageDocumentText(doc: any): string {
    for (const value of [doc?.content, doc?.textContent, doc?.text]) {
        if (typeof value === 'string' && value.trim()) return value;
    }
    return '';
}

/** The fields an operator's corrected text writes onto a row. */
export function buildImageTextEditPatch(doc: any, text: string, now: Date = new Date()): Record<string, any> {
    const prior = doc?.imageText && typeof doc.imageText === 'object' ? doc.imageText : {};
    return {
        id: doc?.id,
        content: text,
        textContent: text,
        ...(typeof doc?.text === 'string' ? { text } : {}),
        imageText: {
            schemaVersion: 1,
            source: prior.source || (doc?.sourcePhotoId ? 'photo' : 'document'),
            ...prior,
            edited: true,
            editedAt: now.toISOString(),
        },
    };
}

/** The fields "Read text" writes onto an image document that had no text. */
export function buildImageDocumentReadPatch(doc: any, text: string, now: Date = new Date()): Record<string, any> {
    const prior = doc?.imageText && typeof doc.imageText === 'object' ? doc.imageText : {};
    return {
        id: doc?.id,
        content: text,
        textContent: text,
        ...(typeof doc?.text === 'string' ? { text } : {}),
        imageText: { ...prior, schemaVersion: 1, source: 'document', kind: 'read', readAt: now.toISOString() },
    };
}

/**
 * Merge `patch` into the row with the same id, or append it when no row has
 * that id. The one write the Reference Files viewer asks its parent for.
 */
export function upsertReferenceDocument(documents: unknown, patch: Record<string, any>): any[] {
    const list = Array.isArray(documents) ? documents : [];
    const id = patch?.id;
    if (id == null || id === '') return list;
    const index = list.findIndex((doc: any) => doc && doc.id === id);
    if (index === -1) return [...list, patch];
    const next = [...list];
    next[index] = { ...list[index], ...patch };
    return next;
}
